import { useState, useEffect } from 'react';
import { Chess } from 'chess.js';
import './ChessBoard.css';

const ChessBoard = ({ pgnString }) => {
    const [game, setGame] = useState(null);
    const [currentMoveIndex, setCurrentMoveIndex] = useState(-1);
    const [moves, setMoves] = useState([]);
    const [autoplay, setAutoplay] = useState(false);
    const [board, setBoard] = useState([]);
    const [whitePlayer, setWhitePlayer] = useState('White');
    const [blackPlayer, setBlackPlayer] = useState('Black');

    // Convert FEN to board array
    const fenToBoard = (fen) => {
        const position = fen.split(' ')[0];
        const rows = position.split('/');
        const boardArray = [];

        rows.forEach(row => {
            const boardRow = [];
            for (let char of row) {
                if (isNaN(char)) {
                    boardRow.push(char);
                } else {
                    for (let i = 0; i < parseInt(char); i++) {
                        boardRow.push(null);
                    }
                }
            }
            boardArray.push(boardRow);
        });

        return boardArray;
    };

    // Get piece symbol
    const getPieceSymbol = (piece) => {
        const symbols = {
            'K': '♔', 'Q': '♕', 'R': '♖', 'B': '♗', 'N': '♘', 'P': '♙',
            'k': '♚', 'q': '♛', 'r': '♜', 'b': '♝', 'n': '♞', 'p': '♟'
        };
        return symbols[piece] || '';
    };

    // Parse PGN headers for player names
    const parsePlayerNames = (pgn) => {
        const whiteMatch = pgn.match(/\[White\s+"([^"]+)"\]/);
        const blackMatch = pgn.match(/\[Black\s+"([^"]+)"\]/);

        return {
            white: whiteMatch ? whiteMatch[1] : 'White',
            black: blackMatch ? blackMatch[1] : 'Black'
        };
    };

    // Load PGN on mount
    useEffect(() => {
        try {
            // Parse player names from PGN
            const players = parsePlayerNames(pgnString);
            setWhitePlayer(players.white);
            setBlackPlayer(players.black);

            const newGame = new Chess();
            newGame.loadPgn(pgnString);
            const history = newGame.history({ verbose: true });
            setMoves(history);
            newGame.reset();
            setGame(newGame);
            setBoard(fenToBoard(newGame.fen()));
            setCurrentMoveIndex(-1);
        } catch (error) {
            console.error('Error loading PGN:', error);
        }
    }, [pgnString]);

    // Navigate to move
    const goToMove = (index) => {
        if (!moves || index < -1 || index >= moves.length) return;

        const newGame = new Chess();

        if (index >= 0) {
            for (let i = 0; i <= index; i++) {
                newGame.move(moves[i].san);
            }
        }

        setGame(newGame);
        setBoard(fenToBoard(newGame.fen()));
        setCurrentMoveIndex(index);
    };

    const firstMove = () => goToMove(-1);
    const prevMove = () => goToMove(currentMoveIndex - 1);
    const nextMove = () => goToMove(currentMoveIndex + 1);
    const lastMove = () => goToMove(moves.length - 1);

    // Autoplay
    useEffect(() => {
        if (autoplay && currentMoveIndex < moves.length - 1) {
            const timer = setTimeout(nextMove, 1000);
            return () => clearTimeout(timer);
        } else if (autoplay && currentMoveIndex >= moves.length - 1) {
            setAutoplay(false);
        }
    }, [autoplay, currentMoveIndex, moves.length]);

    if (!game) {
        return <div className="chess-board-loading">Loading...</div>;
    }

    return (
        <div className="chess-board-container">
            {/* Black player name (top) */}
            <div className="player-label player-black">
                <span className="player-color">●</span> {blackPlayer}
            </div>

            <div className="simple-chessboard">
                {board.map((row, rowIndex) => (
                    <div key={rowIndex} className="board-row">
                        {row.map((piece, colIndex) => {
                            const isLight = (rowIndex + colIndex) % 2 === 0;
                            return (
                                <div
                                    key={`${rowIndex}-${colIndex}`}
                                    className={`board-square ${isLight ? 'light' : 'dark'}`}
                                >
                                    {piece && (
                                        <span className={`piece ${piece === piece.toUpperCase() ? 'white' : 'black'}`}>
                                            {getPieceSymbol(piece)}
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>

            {/* White player name (bottom) */}
            <div className="player-label player-white">
                <span className="player-color">○</span> {whitePlayer}
            </div>

            <div className="chess-controls">
                <button onClick={firstMove} disabled={currentMoveIndex === -1}>⏮</button>
                <button onClick={prevMove} disabled={currentMoveIndex === -1}>◀</button>
                <button
                    onClick={() => setAutoplay(!autoplay)}
                    className={`autoplay-btn ${autoplay ? 'stop' : 'start'}`}
                >
                    {autoplay ? 'Stop' : 'Start'}
                </button>
                <button onClick={nextMove} disabled={currentMoveIndex >= moves.length - 1}>▶</button>
                <button onClick={lastMove} disabled={currentMoveIndex >= moves.length - 1}>⏭</button>
            </div>

            <div className="move-list">
                <div className="move-list-header">
                    {currentMoveIndex === -1 ? (
                        'Starting Position'
                    ) : (
                        <>
                            Move {currentMoveIndex + 1} of {moves.length}: {' '}
                            <strong>{moves[currentMoveIndex]?.san}</strong>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ChessBoard;
